<template>
  <div class="space-y-6">
    <!-- Header -->
    <div>
      <h3 class="text-xl font-bold text-hui-foreground">Визуальные настройки</h3>
      <p class="text-sm text-hui-foreground/60 mt-1">Настройте логотип, цвета и баннеры</p>
    </div>

    <div v-if="loading" class="text-sm text-hui-foreground/60">Загрузка настроек…</div>

    <template v-else>
      <form class="space-y-4" @submit.prevent="saveSettings">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UiKitInput
            v-model="form.primary_color"
            type="color"
            label="Primary Color"
            placeholder="#3B82F6"
          />

          <UiKitInput
            v-model="form.secondary_color"
            type="color"
            label="Secondary Color"
            placeholder="#1F2937"
          />
        </div>

        <UiKitInput
          v-model="form.logo_file"
          label="Logo File"
          placeholder="URL логотипа"
        />

        <UiKitInput
          v-model="form.favicon_file"
          label="Favicon File"
          placeholder="URL фавикона"
        />

        <UiKitInput
          v-model="form.placeholder_file"
          label="Placeholder File"
          placeholder="URL изображения-заглушки"
        />

        <UiKitInput
          v-model="form.banner_img"
          label="Banner Image"
          placeholder="URL баннера"
        />

        <UiKitTextarea
          v-model="form.custom_css"
          label="Custom CSS"
          placeholder="Введите пользовательские CSS стили"
          :rows="6"
        />

        <UiKitTextarea
          v-model="paramsJsonText"
          label="Params JSON"
          placeholder="Введите JSON параметры"
          :rows="4"
        />

        <div class="flex justify-end gap-3 pt-4 border-t border-hui-divider">
          <UiKitButton type="button" color="default" variant="flat" @click="closeModal">
            Отмена
          </UiKitButton>
          <UiKitButton type="submit" color="primary" :loading="loading">
            Сохранить настройки
          </UiKitButton>
        </div>
      </form>
    </template>
  </div>
</template>

<script setup lang="ts">
import consola from 'consola';
import { ref, computed, onMounted } from 'vue';
import { useKitToast } from '@shared/ui/kit';
import { APIAdminInstitution } from '~/fsd-pages/admin/api';
import { useModalStore } from '@shared/model';
import type { VisualSettings } from '@entities/institution';

const emit = defineEmits<{
  'update:size': [size: string]
}>();

const { toast: kitToast } = useKitToast();
const toast = {
  add: (o: { title?: string; description?: string; color?: string; timeout?: number }) =>
    kitToast({ color: o.color === 'green' ? 'success' : o.color === 'red' ? 'danger' : o.color === 'orange' ? 'warning' : 'default', title: o.title, description: o.description, timeout: o.timeout }),
};

const modalStore = useModalStore();
const loading = ref(false);

const form = ref<VisualSettings>({
  logo_file: '',
  favicon_file: '',
  placeholder_file: '',
  primary_color: '',
  secondary_color: '',
  banner_img: '',
  custom_css: '',
  params_json: {},
});

// Computed property for JSON text area
const paramsJsonText = computed({
  get: () => JSON.stringify(form.value.params_json, null, 2),
  set: (value: string) => {
    try {
      form.value.params_json = JSON.parse(value);
    } catch {
      // Keep the text as is if JSON is invalid
    }
  },
});

const loadSettings = async () => {
  try {
    loading.value = true;
    const response = await APIAdminInstitution.getVisualSettings();

    // Данные приходят в объекте visual
    const settings = response.visual || response;

    form.value = { ...settings };
  } catch (error) {
    consola.error('Ошибка загрузки визуальных настроек:', error);
    toast.add({ title: 'Ошибка', description: 'Ошибка загрузки настроек', color: 'red' });
  } finally {
    loading.value = false;
  }
};

const saveSettings = async () => {
  try {
    loading.value = true;

    // Validate JSON field
    try {
      JSON.parse(paramsJsonText.value);
    } catch {
      toast.add({ title: 'Ошибка', description: 'Ошибка в JSON формате', color: 'red' });
      return;
    }

    await APIAdminInstitution.updateVisualSettings(form.value);
    toast.add({ title: 'Сохранено', description: 'Визуальные настройки успешно сохранены', color: 'green' });

    setTimeout(() => {
      closeModal();
    }, 1500);
  } catch (error) {
    consola.error('Ошибка сохранения визуальных настроек:', error);
    toast.add({ title: 'Ошибка', description: 'Ошибка сохранения настроек', color: 'red' });
  } finally {
    loading.value = false;
  }
};

const closeModal = () => {
  modalStore.close();
};

onMounted(() => {
  emit('update:size', 'l');
  loadSettings();
});
</script>
